<template>
  <div class="action-log-sidebar" :class="{ collapsed: isCollapsed }">
    <RotationOrder
      :players="currentLineup"
      :currentPoint="currentPoint"
      @player-substituted="handlePlayerSubstitution"
      @libero-swap-back="handleLiberoSwapBack"
      @libero-sub-in="handleLiberoSubIn"
      @libero-swap="handleLiberoSwap"
      @update-starting-players="updateStartingPlayers"
      :class="{ 'actions-disabled': matchFinished }"
    />
    <div class="sidebar-header">
      <h3 class="sidebar-title">Action Log</h3>
      <button class="btn sidebar-toggle-button" :class="{ collapsed: isCollapsed }" @click="$emit('toggle-collapsed')">
        {{ isCollapsed ? '◀' : '▶' }}
      </button>
    </div>
    <div class="sidebar-content">
      <div v-if="!hasLogEntries" class="sidebar-empty-state">
        No actions logged yet.
      </div>
      <div v-else class="action-log-list">
        <div v-for="point in orderedPoints" :key="point.id || 'current-point'" class="log-point" :class="{ 'just-confirmed': point.id === lastConfirmedPointId }">
          <div class="score-pill" :class="{ hidden: !shouldShowScore(point) }">
            <span class="score-value" :class="getScoreClass(point, 'won')">{{ point.currentScore?.won ?? 0 }}</span>
            <span class="score-value" :class="getScoreClass(point, 'lost')">{{ point.currentScore?.lost ?? 0 }}</span>
          </div>
          <div class="log-entries">
            <div
              v-for="(entry, index) in getOrderedEntries(point)"
              :key="`entry-${point.id}-${index}`"
              class="log-entry"
              :class="[entry.variant]"
            >
              <div class="entry-left">
                <span class="entry-label">{{ entry.label }}</span>
                <button
                  v-if="entry.badge && isRatingEditable(entry)"
                  class="btn entry-badge badge-button"
                  :class="entry.badgeClass"
                  type="button"
                  @click.stop="openRatingMenu(point, entry, index)"
                >
                  {{ entry.badge }}
                </button>
                <span v-else-if="entry.badge" class="entry-badge" :class="entry.badgeClass">{{ entry.badge }}</span>
              </div>
              <div class="entry-right">
                <div
                  v-for="(player, playerIndex) in entry.players"
                  :key="player.key"
                  class="entry-player"
                >
                  <button
                    class="btn player-edit"
                    type="button"
                    :disabled="entry.isLocked"
                    @click.stop="openEntryMenu(point, entry, index, playerIndex)"
                  >
                    <span class="player-name">{{ player.name }}</span>
                    <span v-if="player.number" class="player-number">{{ player.number }}</span>
                  </button>
                </div>
              </div>
              <div
                v-if="isMenuOpen(point, index) && activeMenu?.type === 'player'"
                class="entry-menu"
                @click.stop
              >
                <div class="menu-section">
                  <div class="menu-title">Change player</div>
                  <div v-if="entry.actionType === 'serve'" class="menu-warning">
                    Server should not be changed
                  </div>
                  <button
                    v-for="option in getOnCourtPlayers(point, entry.actionType !== 'serve' && entry.actionType === 'receive' && (entry.label === 'Passing error' || entry.badge === '0'))"
                    :key="option.key"
                    class="btn menu-item"
                    type="button"
                    :disabled="isCurrentPlayerOption(entry, option.player)"
                    @click="updateEntryPlayer(point, entry, option.player)"
                  >
                    <span class="player-number" v-if="option.player.number">{{ option.player.number }}</span>
                    {{ option.player.name }}
                  </button>
                </div>
                <div v-if="entry.actionType === 'duringRally'" class="menu-section">
                  <button class="btn menu-item danger" type="button" @click="removeDuringRallyEntry(point, entry)">
                    Remove action
                  </button>
                </div>
              </div>
              <div
                v-if="isMenuOpen(point, index) && activeMenu?.type === 'rating'"
                class="entry-menu"
                @click.stop
              >
                <div v-if="entry.actionType === 'serve'" class="menu-section">
                  <div class="menu-title">Serve rating</div>
                  <div class="menu-row">
                    <button
                      v-for="rating in [3, 2, 1]"
                      :key="`serve-${rating}`"
                      class="btn menu-item badge"
                      type="button"
                      @click="updateServeRating(point, rating)"
                    >
                      {{ rating }}
                    </button>
                  </div>
                </div>
                <div v-if="entry.actionType === 'receive'" class="menu-section">
                  <div class="menu-title">Pass rating</div>
                  <div class="menu-row">
                    <button
                      v-for="rating in [3, 2, 1]"
                      :key="`receive-${rating}`"
                      class="btn menu-item badge"
                      type="button"
                      @click="updateReceiveRating(point, rating)"
                    >
                      {{ rating }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="point-divider"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { store } from '../store.js';
import RotationOrder from './RotationOrder.vue';
export default {
  name: 'ActionLogSidebar',
  components: {
    RotationOrder,
  },
  props: {
    isCollapsed: {
      type: Boolean,
      default: false
    },
    points: {
      type: Array,
      default: () => []
    },
    currentPoint: {
      type: Object,
      default: null
    },
    currentLineup: {
      type: Array,
      default: () => []
    },
    lastConfirmedPointId: {
      type: Number,
      default: null
    },
    sidebarCollapsed: {
      type: Boolean,
      default: false
    },
    matchFinished: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeMenu: null
    };
  },
  computed: {
    hasLogEntries() {
      if (this.points.length) {
        return true;
      }
      if (!this.currentPoint) {
        return false;
      }
      return this.buildPointEntries(this.currentPoint).length > 0;
    },
    orderedPoints() {
      const history = [...this.points].reverse();
      if (!this.currentPoint) {
        return history;
      }
      return [this.currentPoint, ...history];
    }
  },
  mounted() {
    document.addEventListener('click', this.closeMenu);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeMenu);
  },
  methods: {
    // Forward RotationOrder events to parent (App.vue)
    handlePlayerSubstitution(payload) {
      this.$emit('player-substituted', payload);
    },
    handleLiberoSwapBack(payload) {
      this.$emit('libero-swap-back', payload);
    },
    handleLiberoSubIn(payload) {
      this.$emit('libero-sub-in', payload);
    },
    handleLiberoSwap(payload) {
      this.$emit('libero-swap', payload);
    },
    updateStartingPlayers(payload) {
      this.$emit('update-starting-players', payload);
    },
    openEntryMenu(point, entry, entryIndex, playerIndex) {
      const pointKey = point.id || 'current-point';
      this.activeMenu = {
        pointKey,
        entryIndex,
        playerIndex,
        entry,
        type: 'player'
      };
    },
    openRatingMenu(point, entry, entryIndex) {
      const pointKey = point.id || 'current-point';
      this.activeMenu = {
        pointKey,
        entryIndex,
        playerIndex: null,
        entry,
        type: 'rating'
      };
    },
    isMenuOpen(point, entryIndex) {
      const pointKey = point.id || 'current-point';
      return Boolean(this.activeMenu && this.activeMenu.pointKey === pointKey && this.activeMenu.entryIndex === entryIndex);
    },
    closeMenu() {
      this.activeMenu = null;
    },
    isCurrentPoint(point) {
      return this.currentPoint === point;
    },
    isRatingEditable(entry) {
      if (entry.actionType !== 'serve' && entry.actionType !== 'receive') {
        return false;
      }
      const badgeValue = String(entry.badge || '').trim();
      return ['1', '2', '3'].includes(badgeValue);
    },
    isCurrentPlayerOption(entry, player) {
      if (!entry || !player) {
        return false;
      }
      if (player.name === 'Team') {
        return entry.actionType === 'receive' && entry.label === 'Passing error' && entry.players?.[0]?.key === 'team-receive';
      }
      return entry.players?.some(item => item.key === (player.id || player.number));
    },
    getOnCourtPlayers(point, includeTeam = true) {
      const onCourt = Array.isArray(point.currentOnCourtNumbers) ? point.currentOnCourtNumbers : [];
      const rosterMap = point.duringRallyRoster || {};
      const fallback = this.currentLineup || [];
      const players = onCourt
        .map(number => {
          let name = rosterMap[number] || (fallback.find(item => item.player && item.player.number === number)?.player?.name || '');
          let player = fallback.find(item => item.player && item.player.number === number)?.player;
          if (!player && store.currentLibero && store.currentLibero.number === number) {
            player = store.currentLibero;
            name = player.name || '';
          }
          if (!player) {
            player = { number, name };
          }
          return {
            key: player.id || number,
            player
          };
        })
        .filter(item => item.player && item.player.number !== undefined);
      if (includeTeam) {
        players.push({ key: 'team', player: { name: 'Team' } });
      }
      return players;
    },
    updateEntryPlayer(point, entry, player) {
      if (!player) {
        return;
      }
      if (entry.actionType === 'serve') {
        point.serveAction.player = player;
        this.closeMenu();
        return;
      }
      if (entry.actionType === 'receive') {
        if (player.name === 'Team') {
          point.receiveAction.player = 'Team receive error';
          point.receiveAction.rating = 0;
          if (point.scoreAction && point.scoreAction.pointDetail === 'Opp. ace') {
            point.scoreAction.player = 'Team';
          }
        } else {
          point.receiveAction.player = player;
          if (point.scoreAction && point.scoreAction.pointDetail === 'Opp. ace') {
            point.scoreAction.player = player;
          }
        }
        this.closeMenu();
        return;
      }
      if (entry.actionType === 'outcome') {
        if (entry.isAssist) {
          if (this.activeMenu && this.activeMenu.playerIndex === 1) {
            point.scoreAction.secondPlayer = player;
          } else {
            point.scoreAction.player = player;
          }
        } else {
          point.scoreAction.player = player;
          if (point.scoreAction && point.scoreAction.pointDetail === 'Opp. ace') {
            if (player.name === 'Team') {
              point.receiveAction.player = 'Team receive error';
              point.receiveAction.rating = 0;
            } else {
              point.receiveAction.player = player;
              point.receiveAction.rating = 0;
            }
          }
        }
        this.closeMenu();
        return;
      }
      if (entry.actionType === 'duringRally') {
        this.updateDuringRallyPlayer(point, entry, player);
        this.closeMenu();
      }
    },
    updateDuringRallyPlayer(point, entry, player) {
      const number = player.number;
      if (!number) {
        return;
      }
      if (entry.source === 'events') {
        const events = point.duringRallyEvents || [];
        if (events[entry.eventIndex]) {
          const previousNumber = events[entry.eventIndex].number;
          const eventType = events[entry.eventIndex].type;
          events[entry.eventIndex].number = number;
          const bucket = point.duringRally?.[eventType] || {};
          if (previousNumber && bucket[previousNumber]) {
            const nextCount = bucket[previousNumber] - 1;
            if (nextCount <= 0) {
              delete bucket[previousNumber];
            } else {
              bucket[previousNumber] = nextCount;
            }
          }
          bucket[number] = (bucket[number] || 0) + 1;
        }
        return;
      }
      if (entry.source === 'counts') {
        const bucket = point.duringRally?.[entry.eventType] || {};
        const oldNumber = entry.number;
        if (oldNumber !== undefined && bucket[oldNumber]) {
          const nextCount = bucket[oldNumber] - 1;
          if (nextCount <= 0) {
            delete bucket[oldNumber];
          } else {
            bucket[oldNumber] = nextCount;
          }
          bucket[number] = (bucket[number] || 0) + 1;
        }
      }
    },
    updateServeRating(point, rating) {
      point.serveAction.rating = rating;
      point.serveAction.call = null;
      this.closeMenu();
    },
    updateReceiveRating(point, rating) {
      point.receiveAction.rating = rating;
      point.receiveAction.call = null;
      this.closeMenu();
    },
    removeDuringRallyEntry(point, entry) {
      if (entry.source === 'events') {
        const events = point.duringRallyEvents || [];
        if (events[entry.eventIndex]) {
          const eventType = events[entry.eventIndex].type;
          const eventNumber = events[entry.eventIndex].number;
          events.splice(entry.eventIndex, 1);
          const bucket = point.duringRally?.[eventType] || {};
          if (eventNumber && bucket[eventNumber]) {
            const nextCount = bucket[eventNumber] - 1;
            if (nextCount <= 0) {
              delete bucket[eventNumber];
            } else {
              bucket[eventNumber] = nextCount;
            }
          }
        }
        this.closeMenu();
        return;
      }
      if (entry.source === 'counts') {
        const bucket = point.duringRally?.[entry.eventType] || {};
        const oldNumber = entry.number;
        if (oldNumber !== undefined && bucket[oldNumber]) {
          const nextCount = bucket[oldNumber] - 1;
          if (nextCount <= 0) {
            delete bucket[oldNumber];
          } else {
            bucket[oldNumber] = nextCount;
          }
        }
        this.closeMenu();
      }
    },
    shouldShowScore(point) {
      return Boolean(point && point.scoreAction && point.scoreAction.outcome);
    },
    getScoreClass(point, side) {
      if (!point || !point.scoreAction || !point.scoreAction.outcome) {
        return '';
      }
      const outcome = point.scoreAction.outcome;
      if (side === 'won' && outcome === 'Won') {
        return 'score-won';
      }
      if (side === 'lost' && outcome === 'Lost') {
        return 'score-lost';
      }
      return '';
    },
    getOrderedEntries(point) {
      return [...this.buildPointEntries(point)].reverse();
    },
    buildPointEntries(point) {
      const entries = [];
      if (!point) {
        return entries;
      }

      const detail = point.scoreAction?.pointDetail || '';
      const skipServeReceive = ['Opp. serve error', 'Opp. ace', 'Ace', 'Serve error'].includes(detail);

      if (!skipServeReceive && point.startPoint === 'Receive') {
        const receiveEntry = this.getReceiveEntry(point);
        if (receiveEntry) {
          entries.push(receiveEntry);
        }
      } else if (!skipServeReceive && point.startPoint === 'Serve') {
        const serveEntry = this.getServeEntry(point);
        if (serveEntry) {
          entries.push(serveEntry);
        }
      }

      const rallyEntries = this.getDuringRallyEntries(point);
      entries.push(...rallyEntries);

      const outcomeEntry = this.getOutcomeEntry(point);
      if (outcomeEntry) {
        entries.push(outcomeEntry);
      }

      return entries;
    },
    getServeEntry(point) {
      const serve = point.serveAction || {};
      const player = this.getPlayerDisplay(serve.player);
      if (!player) {
        return null;
      }
      const rotationErrors = ['Opposition Rotation Error', 'Rotation Error'];
      if (serve.call && rotationErrors.includes(serve.call)) {
        return this.buildEntry('Serve', 'X', [player], '', { actionType: 'serve' });
      }
      if (serve.call && !rotationErrors.includes(serve.call)) {
        return this.buildEntry(serve.call, null, [player], '', { actionType: 'serve' });
      }
      if (serve.rating === null || serve.rating === undefined) {
        const badge = this.isCurrentPoint(point) ? '?' : 'X';
        return this.buildEntry('Serve', badge, [player], '', { actionType: 'serve' });
      }
      if (serve.rating === 0) {
        return this.buildEntry('Serve error', '0', [player], 'error', { actionType: 'serve' });
      }
      if (serve.rating === 4) {
        return this.buildEntry('Ace', '4', [player], 'success', { actionType: 'serve' });
      }
      return this.buildEntry('Serve', String(serve.rating), [player], '', { actionType: 'serve' });
    },
    getReceiveEntry(point) {
      const receive = point.receiveAction || {};
      const player = this.getPlayerDisplay(receive.player);
      const rotationErrors = ['Opposition Rotation Error', 'Rotation Error'];
      if (receive.call && rotationErrors.includes(receive.call)) {
        return this.buildEntry('Receive', 'X', [], '', { actionType: 'receive' });
      }
      if (receive.player && receive.player.name === 'Opp. serve error') {
        return this.buildEntry('Opp. serve error', null, [], '', { actionType: 'receive' });
      }
      if (receive.player === 'Team receive error' || receive.rating === 0) {
        return this.buildEntry('Passing error', '0', player ? [player] : [], 'error', { actionType: 'receive' });
      }
      if (receive.rating === null || receive.rating === undefined) {
        const badge = this.isCurrentPoint(point) ? '?' : 'X';
        return this.buildEntry('Receive', badge, player ? [player] : [], '', { actionType: 'receive' });
      }
      return this.buildEntry('Pass', String(receive.rating), player ? [player] : [], '', { actionType: 'receive' });
    },
    getDuringRallyEntries(point) {
      const events = point.duringRallyEvents || [];
      if (events.length) {
        return events.map((event, index) => {
          const label = this.getEventLabel(event.type);
          const player = this.getPlayerDisplayByNumber(point, event.number);
          return {
            label,
            badge: null,
            badgeClass: '',
            players: player ? [player] : [],
            variant: '',
            key: `${event.type}-${event.number}-${index}`,
            actionType: 'duringRally',
            source: 'events',
            eventType: event.type,
            eventIndex: index,
            number: event.number
          };
        });
      }
      const entries = [];
      const during = point.duringRally || {};
      entries.push(...this.getCountEntries(point, during.attacks || {}, 'Attack attempt', 'attacks'));
      entries.push(...this.getCountEntries(point, during.blocksSolo || {}, 'Block (solo)', 'blocksSolo'));
      entries.push(...this.getCountEntries(point, during.blocksJoint || {}, 'Block (joint)', 'blocksJoint'));
      entries.push(...this.getCountEntries(point, during.digs || {}, 'Dig', 'digs'));
      return entries;
    },
    getEventLabel(type) {
      if (type === 'attacks') {
        return 'Attack attempt';
      }
      if (type === 'blocksSolo') {
        return 'Block (solo)';
      }
      if (type === 'blocksJoint') {
        return 'Block (joint)';
      }
      if (type === 'digs') {
        return 'Dig';
      }
      return type;
    },
    getCountEntries(point, bucket, label, eventType) {
      return Object.entries(bucket)
        .filter(([, count]) => count > 0)
        .map(([number, count]) => {
          const player = this.getPlayerDisplayByNumber(point, Number(number));
          const badge = count > 1 ? String(count) : null;
          return this.buildEntry(label, badge, player ? [player] : [], '', {
            actionType: 'duringRally',
            source: 'counts',
            eventType,
            number: Number(number)
          });
        });
    },
    getOutcomeEntry(point) {
      const scoreAction = point.scoreAction || {};
      if (!scoreAction.outcome) {
        return null;
      }
      const variant = scoreAction.outcome === 'Won' ? 'won' : 'lost';
      const detail = scoreAction.pointDetail || '';
      const isRotationError = detail === 'Rotation Error';
      if (detail === 'Kill block (assist)') {
        const players = [];
        const primary = this.getPlayerDisplay(scoreAction.player);
        const secondary = this.getPlayerDisplay(scoreAction.secondPlayer);
        if (primary) {
          players.push(primary);
        }
        if (secondary) {
          players.push(secondary);
        }
        return this.buildEntry(detail || 'Point', null, players, variant, { actionType: 'outcome', isAssist: true, isLocked: isRotationError });
      }
      const player = this.getPlayerDisplay(scoreAction.player);
      return this.buildEntry(detail || 'Point', null, player ? [player] : [], variant, { actionType: 'outcome', isAssist: false, isLocked: isRotationError });
    },
    buildEntry(label, badge, players, variant = '', meta = {}) {
      return {
        label,
        badge,
        badgeClass: badge ? (variant === 'error' ? 'badge-error' : variant === 'success' ? 'badge-success' : '') : '',
        players,
        variant,
        ...meta
      };
    },
    getPlayerDisplay(player) {
      if (!player) {
        return null;
      }
      if (player === 'Team') {
        return { key: 'team', name: 'Team', number: null };
      }
      if (player === 'Team receive error') {
        return { key: 'team-receive', name: 'Team', number: null };
      }
      if (player.name === 'Opp. serve error') {
        return null;
      }
      if (player.number === undefined) {
        return null;
      }
      return {
        key: player.id || player.number,
        name: player.name || '',
        number: player.number
      };
    },
    getPlayerDisplayByNumber(point, number) {
      if (!number) {
        return null;
      }
      const rosterMap = point.duringRallyRoster || {};
      if (rosterMap[number]) {
        return { key: number, name: rosterMap[number], number };
      }
      if (point.currentLiberoNumber && store.currentLibero && store.currentLibero.number === number) {
        return {
          key: store.currentLibero.id || number,
          name: store.currentLibero.name || '',
          number
        };
      }
      const livePlayer = this.currentLineup.find(item => item.player && item.player.number === number);
      if (livePlayer && livePlayer.player) {
        return { key: livePlayer.player.id || number, name: livePlayer.player.name || '', number };
      }
      return { key: number, name: '', number };
    }
  }
};
</script>

<style scoped lang="scss">
.action-log-sidebar {
  position: fixed;
  top: 0;
  right: 0;
  width: 380px;
  height: 100vh;
  background-color: var(--grey-5);
  z-index: 100;
  transition: transform 0.3s ease;

  &.collapsed {
    transform: translateX(340px);

    .sidebar-content {
      transform: translateX(40px);
    }
  }
}

.sidebar-header {
  padding: 8px 16px;
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  align-items: center;

  .sidebar-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }

  .sidebar-toggle-button {
    background-color: transparent;
    position: absolute;
    left: 20px;
    top: 8px;
    color: var(--grey-700);
    border: none;
    padding: 0;
    margin: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    cursor: pointer;
    font-size: 10px;
    font-weight: 600;
    transform: translateX(-12px);
    transition: all 0.2s ease;
    
    &:hover {
      background-color: var(--grey-100);
    }

    &.collapsed {
      transform: translateX(-16px);
    }
  }
}


.sidebar-content {
  padding: 16px;
  transition: transform 0.3s ease;
  overflow-y: auto;
  height: calc(100% - 232px);
}

.sidebar-empty-state {
  margin: 0;
  color: var(--grey-400);
}

.action-log-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.log-point {
  display: grid;
  grid-template-columns: 50px 1fr;
  gap: 16px;
  transition: background-color 0.3s ease;
  padding: 8px;
  border-radius: 12px;

  &:nth-child(even) {
    background-color: var(--grey-15);
  }
}

.log-point.just-confirmed {
  background-color: var(--grey-50);
  animation: highlight-fade 2s ease-out;
}

.score-pill {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--white);
  font-weight: 700;
  font-size: 14px;
  gap: 2px;
  height: fit-content;
  width: fit-content;
  margin: 2px;
}

.score-pill.hidden {
  opacity: 0.5;
}

.score-value {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--grey-200);
  background-color: var(--grey-35);
  border-radius: 5px;
  font-weight: 700;
  letter-spacing: -0.05rem;
}

.score-value.score-won {
  background: var(--green-500);
  color: var(--white);
}

.score-value.score-lost {
  background: var(--red-300);
  color: var(--white);
}

.log-entries {
  display: flex;
  flex-direction: column;
  gap: 1px;
  color: var(--grey-800);
}

.log-entry {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
  min-height: 26px;
  position: relative;
}

.log-entry.won .entry-label {
  color: var(--green-500);
  font-size: 12px;
}

.log-entry.lost .entry-label {
  color: var(--red-300);
  font-size: 12px;
}

.entry-left {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  min-height: 26px;
}

.entry-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  border-radius: 6px;
  background: var(--grey-20);
  color: var(--grey-800);
  font-size: 12px;
  font-weight: 700;
}

.entry-badge.badge-button {
  border: none;
  padding: 0 6px;
  cursor: pointer;
}

.entry-badge.badge-error {
  background: var(--red-50);
  color: var(--red-300);
}

.entry-badge.badge-success {
  background: var(--green-100);
  color: var(--green-500);
}

.entry-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: 2px;
  min-height: 26px;
}

.entry-player {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}

.player-edit {
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer; 
  font-weight: 600;
  height: auto;
  padding: 2px 2px 2px 6px;

  .player-name {
    font-size: 12px;
  }
}

.entry-menu {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 200;
  background: var(--white);
  border-radius: 12px;
  padding: 8px;
  min-width: 200px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.12);
}

.menu-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 8px;
}

.menu-section:last-child {
  margin-bottom: 0;
}

.menu-title {
  font-size: 11px;
  font-weight: 700;
  color: var(--grey-600);
}

.menu-warning {
  font-size: 11px;
  color: var(--red-300);
  font-weight: 600;
}

.menu-row {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.menu-item {
  background: var(--grey-10);
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  height: auto;
  font-size: 12px;
  cursor: pointer;
  text-align: left;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.menu-item:disabled {
  background: var(--grey-25);
  color: var(--grey-300);
  cursor: not-allowed;
}

.menu-item.badge {
  justify-content: center;
  min-width: 36px;
}

.menu-item.danger {
  background: var(--red-50);
  color: var(--red-600);
}

.player-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  margin: 0;
  height: 24px;
  border-radius: 50%;
  border: 1px solid var(--grey-50);
  background-color: var(--white);
  font-size: 12px;
  font-weight: 900;
}

.point-divider {
  grid-column: 1 / -1;
  height: 1px;
  background: var(--grey-75);
  margin: -3px 0;
  display: none;
}

@keyframes highlight-fade {
  0% {
    background-color: var(--grey-400);
  }
  100% {
    background-color: var(--grey-20);
  }
}
</style>